import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Navigation from '../components/Navigation';
import Logo from '../components/Logo';
import { ListFooter } from '../ui';

const NET_MINS = 510;
const effColor = (e) => !e ? '#888' : e >= 100 ? '#22c55e' : e >= 85 ? '#f59e0b' : '#ef4444';
const effBg = (e) => !e ? '#f3f4f6' : e >= 100 ? '#dcfce7' : e >= 85 ? '#fef9c3' : '#fee2e2';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'}) : '-';

export function IEContent() {
  const { user } = useAuth();
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState({ style_sam:[], top_operations:[], division_stats:[], summary:{} });
  const [standardSAM, setStandardSAM] = useState([]);
  const [samFilter, setSamFilter] = useState('');
  const [samDivision, setSamDivision] = useState('');
  const [editingSAM, setEditingSAM] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [bulletinAnalysis, setBulletinAnalysis] = useState([]);
  const [operatorPerf, setOperatorPerf] = useState([]);
  const [bulletins, setBulletins] = useState([]);
  const [selectedBulletin, setSelectedBulletin] = useState(null);
  const [lineBalance, setLineBalance] = useState(null);
  const [saving, setSaving] = useState(false);
  const [machinesMaster, setMachinesMaster] = React.useState([]);
  const [machinesSummary, setMachinesSummary] = React.useState([]);
  const [editingMachine, setEditingMachine] = React.useState(null);
  const [machineForm, setMachineForm] = React.useState({});

  const tabs = [
    {key:'dashboard',label:'📊 SAM Dashboard'},
    {key:'standard',label:'📋 Standard SAM'},
    {key:'analysis',label:'📈 Bulletin Analysis'},
    {key:'operator',label:'🏆 Operator Performance'},
    {key:'balancing',label:'⚖️ Line Balancing'},
    {key:'machines',label:'🔧 Machines Master'},
  ];

  useEffect(() => {
    if(tab==='dashboard') api.get('/ie/dashboard').then(r=>setDashboard(r.data)).catch(()=>{});
    if(tab==='standard') fetchStandardSAM();
    if(tab==='analysis') api.get('/ie/bulletin-analysis').then(r=>setBulletinAnalysis(r.data.data||[])).catch(()=>{});
    if(tab==='operator') api.get('/ie/operator-performance').then(r=>setOperatorPerf(r.data.data||[])).catch(()=>{});
    if(tab==='balancing') api.get('/bulletins').then(r=>setBulletins(r.data.data||[])).catch(()=>{});
    if(tab==='machines') api.get('/ie/machines-master').then(r=>{setMachinesMaster(r.data.data||[]);setMachinesSummary(r.data.summary||[]);}).catch(()=>{});
  }, [tab]);

  const fetchStandardSAM = async () => {
    try {
      const r = await api.get('/ie/standard-sam');
      setStandardSAM(r.data.data||[]);
    } catch(e) {}
  };

  const loadLineBalance = async (pb_id) => {
    try {
      const r = await api.get('/ie/line-balancing/'+pb_id);
      setLineBalance(r.data);
      setSelectedBulletin(pb_id);
    } catch(e) { alert('Error loading line balance'); }
  };

  const saveSAM = async (id) => {
    const v = parseFloat(editValue);
    if (v > 0 && v < 0.1 && !window.confirm(`Standard SAM ${v} min is under 0.1 min (6 seconds) — nothing on a garment is that fast. Check it isn't in the wrong unit (seconds/minutes). Save anyway?`)) return;
    setSaving(true);
    try {
      await api.patch('/ie/standard-sam/'+id, { standard_sam: editValue });
      setEditingSAM(null); setEditValue('');
      fetchStandardSAM();
    } catch(e) { alert('Error saving SAM'); }
    finally { setSaving(false); }
  };

  const saveMachine = async (id) => {
    setSaving(true);
    try {
      await api.patch('/ie/machines-master/'+id, machineForm);
      setEditingMachine(null);
      api.get('/ie/machines-master').then(r=>{setMachinesMaster(r.data.data||[]);setMachinesSummary(r.data.summary||[]);});
    } catch(e) { alert('Error'); }
    finally { setSaving(false); }
  };

  const filteredSAM = standardSAM.filter(op => {
    const matchDiv = !samDivision || op.division === samDivision;
    const matchSearch = !samFilter || op.process_name.toLowerCase().includes(samFilter.toLowerCase()) || (op.process_code||'').toLowerCase().includes(samFilter.toLowerCase());
    return matchDiv && matchSearch;
  });

  const inputStyle = {padding:'6px 10px',border:'1px solid #ddd',borderRadius:5,fontSize:12};
  const labelStyle = {fontSize:11,color:'#888',fontWeight:600,display:'block',marginBottom:4,textTransform:'uppercase'};

  return (
    <>
      <div style={{padding:24}}>
        <div style={{background:'white',borderRadius:10,boxShadow:'none',border:'1px solid #E8E7E7'}}>
          {/* Tab Bar */}
          <div style={{display:'flex',padding:'0 20px',borderBottom:'1px solid #eee',overflowX:'auto'}}>
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{padding:'14px 18px',border:'none',background:'transparent',cursor:'pointer',fontSize:13,fontWeight:tab===t.key?600:400,color:tab===t.key?'#3B3A8F':'#888',borderBottom:tab===t.key?'2px solid #3B3A8F':'2px solid transparent',whiteSpace:'nowrap'}}>{t.label}</button>
            ))}
          </div>

          <div style={{padding:20}}>

            {/* SAM DASHBOARD */}
            {tab==='dashboard' && (
              <div>
                {/* Summary Cards */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,marginBottom:24}}>
                  {[
                    {label:'Total Bulletins',value:dashboard.summary?.total_bulletins||0,color:'#3B3A8F'},
                    {label:'Unique Styles',value:dashboard.summary?.total_styles||0,color:'#2A2970'},
                    {label:'Avg SAM (mins)',value:dashboard.summary?.avg_sam?Number(dashboard.summary.avg_sam).toFixed(1):'-',color:'#E22D2D'},
                    {label:'Total Machines Planned',value:dashboard.summary?.total_machines_planned||0,color:'#3B3A8F'},
                  ].map(card => (
                    <div key={card.label} style={{background:'white',border:'1px solid #eee',borderRadius:8,padding:'14px 16px'}}>
                      <div style={{fontSize:11,color:'#888',fontWeight:700,textTransform:'uppercase'}}>{card.label}</div>
                      <div style={{fontSize:28,fontWeight:900,color:card.color,marginTop:4}}>{card.value}</div>
                    </div>
                  ))}
                </div>

                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
                  {/* Style-wise SAM */}
                  <div>
                    <div style={{fontWeight:700,fontSize:14,marginBottom:12,color:'#333'}}>Style-wise SAM Comparison</div>
                    {dashboard.style_sam.length===0 ? (
                      <div style={{padding:30,textAlign:'center',color:'#aaa',border:'1px dashed #eee',borderRadius:8}}>No bulletin data yet</div>
                    ) : (
                      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                        <thead><tr style={{background:'#F7F6F3'}}>
                          {['Style','Division','Buyer','Bulletins','Avg SAM','Min','Max'].map(h=>(
                            <th key={h} style={{padding:'7px 10px',textAlign:'left',color:'#555',fontWeight:600,borderBottom:'2px solid #eee',fontSize:11}}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {dashboard.style_sam.map((r,i)=>(
                            <tr key={i} style={{borderBottom:'1px solid #f5f5f5'}}>
                              <td style={{padding:'7px 10px',fontWeight:600}}>{r.style}</td>
                              <td style={{padding:'7px 10px'}}>{r.division}</td>
                              <td style={{padding:'7px 10px'}}>{r.buyer}</td>
                              <td style={{padding:'7px 10px',textAlign:'center'}}>{r.bulletin_count}</td>
                              <td style={{padding:'7px 10px',fontWeight:700,color:'#3B3A8F'}}>{Number(r.avg_sam||0).toFixed(2)}</td>
                              <td style={{padding:'7px 10px',color:'#22c55e'}}>{Number(r.min_sam||0).toFixed(2)}</td>
                              <td style={{padding:'7px 10px',color:'#ef4444'}}>{Number(r.max_sam||0).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Top Operations by SAM */}
                  <div>
                    <div style={{fontWeight:700,fontSize:14,marginBottom:12,color:'#333'}}>Top 20 Operations by Time</div>
                    {dashboard.top_operations.length===0 ? (
                      <div style={{padding:30,textAlign:'center',color:'#aaa',border:'1px dashed #eee',borderRadius:8}}>No data yet</div>
                    ) : (
                      <div style={{maxHeight:400,overflowY:'auto'}}>
                        {dashboard.top_operations.map((op,i)=>{
                          const pct = Math.min(100, (parseFloat(op.avg_sam)||0) / (parseFloat(dashboard.top_operations[0]?.avg_sam)||1) * 100);
                          return (
                            <div key={i} style={{marginBottom:8,padding:'8px 12px',background:'#F7F6F3',borderRadius:6}}>
                              <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                                <span style={{fontSize:12,fontWeight:600}}>{op.process_name}</span>
                                <span style={{fontSize:12,fontWeight:700,color:'#3B3A8F'}}>{Number(op.avg_sam||0).toFixed(2)}m</span>
                              </div>
                              <div style={{height:6,background:'#e5e7eb',borderRadius:3}}>
                                <div style={{height:'100%',width:pct+'%',background:'#3B3A8F',borderRadius:3}}/>
                              </div>
                              <div style={{display:'flex',justifyContent:'space-between',marginTop:3,fontSize:10,color:'#888'}}>
                                <span>{op.machine_code} · Used {op.usage_count}x</span>
                                <span>Min:{Number(op.min_sam||0).toFixed(1)} Max:{Number(op.max_sam||0).toFixed(1)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* STANDARD SAM */}
            {tab==='standard' && (
              <div>
                <div style={{display:'flex',gap:12,marginBottom:16,alignItems:'center',flexWrap:'wrap'}}>
                  <input value={samFilter} onChange={e=>setSamFilter(e.target.value)} placeholder="Search operation..." style={{...inputStyle,width:200}}/>
                  <select value={samDivision} onChange={e=>setSamDivision(e.target.value)} style={inputStyle}>
                    <option value="">All Divisions</option>
                    <option value="MENS">Mens</option>
                    <option value="LADIES">Ladies</option>
                    <option value="KIDS">Kids</option>
                  </select>
                  <span style={{fontSize:12,color:'#888'}}>{filteredSAM.length} operations</span>
                </div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead><tr style={{background:'#F7F6F3'}}>
                    {['Division','Code','Operation','Machine','Standard SAM','Avg Observed','Best','Worst','Efficiency','Usage','Action'].map(h=>(
                      <th key={h} style={{padding:'8px 10px',textAlign:'left',color:'#555',fontWeight:600,borderBottom:'2px solid #eee',fontSize:11}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {filteredSAM.length===0 ? (
                      <tr><td colSpan={11} style={{padding:30,textAlign:'center',color:'#aaa'}}>No operations found</td></tr>
                    ) : filteredSAM.map((op,i)=>(
                      <tr key={i} style={{borderBottom:'1px solid #f5f5f5'}}>
                        <td style={{padding:'8px 10px'}}>{op.division}</td>
                        <td style={{padding:'8px 10px',fontFamily:'monospace',fontSize:11,color:'#888'}}>{op.process_code}</td>
                        <td style={{padding:'8px 10px',fontWeight:600}}>{op.process_name}</td>
                        <td style={{padding:'8px 10px'}}>{op.machine_code}</td>
                        <td style={{padding:'8px 10px'}}>
                          {editingSAM===op.id ? (
                            <div style={{display:'flex',gap:4}}>
                              <input type="number" step="0.01" value={editValue} onChange={e=>setEditValue(e.target.value)}
                                style={{width:60,padding:'3px 6px',border:'1px solid #3B3A8F',borderRadius:4,fontSize:12}}/>
                              <button onClick={()=>saveSAM(op.id)} disabled={saving} style={{padding:'2px 8px',background:'#3B3A8F',color:'white',border:'none',borderRadius:4,fontSize:11,cursor:'pointer'}}>✓</button>
                              <button onClick={()=>setEditingSAM(null)} style={{padding:'2px 8px',background:'#f0f2f8',border:'1px solid #ddd',borderRadius:4,fontSize:11,cursor:'pointer'}}>✕</button>
                            </div>
                          ) : (
                            <span style={{fontWeight:700,color:op.standard_sam?'#3B3A8F':'#aaa'}}>{op.standard_sam?op.standard_sam+'m':'Not set'}</span>
                          )}
                        </td>
                        <td style={{padding:'8px 10px',color:'#22c55e',fontWeight:600}}>{op.avg_observed_sam?Number(op.avg_observed_sam).toFixed(2)+'m':'-'}</td>
                        <td style={{padding:'8px 10px',color:'#22c55e'}}>{op.min_observed_sam?Number(op.min_observed_sam).toFixed(2)+'m':'-'}</td>
                        <td style={{padding:'8px 10px',color:'#ef4444'}}>{op.max_observed_sam?Number(op.max_observed_sam).toFixed(2)+'m':'-'}</td>
                        <td style={{padding:'8px 10px'}}>
                          {op.efficiency_pct ? (
                            <span style={{padding:'2px 8px',borderRadius:10,fontSize:11,fontWeight:600,background:effBg(op.efficiency_pct),color:effColor(op.efficiency_pct)}}>{op.efficiency_pct}%</span>
                          ) : '-'}
                        </td>
                        <td style={{padding:'8px 10px',textAlign:'center'}}>{op.usage_count||0}</td>
                        <td style={{padding:'8px 10px'}}>
                          <button onClick={()=>{setEditingSAM(op.id);setEditValue(op.standard_sam||'');}}
                            style={{padding:'3px 10px',background:'#EEEDF8',border:'none',borderRadius:4,color:'#3B3A8F',cursor:'pointer',fontSize:11}}>Edit SAM</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <ListFooter shown={filteredSAM.length} total={standardSAM.length} noun="operations" />
              </div>
            )}

            {/* BULLETIN ANALYSIS */}
            {tab==='analysis' && (
              <div>
                <div style={{fontWeight:700,fontSize:14,marginBottom:16,color:'#333'}}>Bulletin-wise SAM Analysis</div>
                {bulletinAnalysis.length===0 ? (
                  <div style={{padding:40,textAlign:'center',color:'#aaa',border:'1px dashed #eee',borderRadius:8}}>No bulletins created yet</div>
                ) : (
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead><tr style={{background:'#F7F6F3'}}>
                      {['PB No','GPO','Style','Buyer','Div','Order Qty','Std SAM','Observed SAM','Variance','Machines','Daily Target','TAT','Critical Op','Status'].map(h=>(
                        <th key={h} style={{padding:'8px 10px',textAlign:'left',color:'#555',fontWeight:600,borderBottom:'2px solid #eee',fontSize:11,whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {bulletinAnalysis.map((pb,i)=>{
                        const variance = pb.observed_sam && pb.total_sam ? (parseFloat(pb.observed_sam) - parseFloat(pb.total_sam)).toFixed(2) : null;
                        return (
                          <tr key={i} style={{borderBottom:'1px solid #f5f5f5'}}>
                            <td style={{padding:'8px 10px',fontFamily:'monospace',fontWeight:600,color:'#3B3A8F'}}>{pb.pb_no}</td>
                            <td style={{padding:'8px 10px',fontWeight:600}}>{pb.gpo_no}</td>
                            <td style={{padding:'8px 10px'}}>{pb.style}</td>
                            <td style={{padding:'8px 10px'}}>{pb.buyer}</td>
                            <td style={{padding:'8px 10px'}}>{pb.division}</td>
                            <td style={{padding:'8px 10px',textAlign:'right'}}>{Number(pb.order_qty||0).toLocaleString('en-IN')}</td>
                            <td style={{padding:'8px 10px',fontWeight:700,color:'#3B3A8F'}}>{pb.total_sam?Number(pb.total_sam).toFixed(2):'-'}</td>
                            <td style={{padding:'8px 10px',fontWeight:700,color:'#22c55e'}}>{pb.observed_sam?Number(pb.observed_sam).toFixed(2):'-'}</td>
                            <td style={{padding:'8px 10px',fontWeight:700,color:variance>0?'#ef4444':variance<0?'#22c55e':'#888'}}>{variance?variance+'m':'-'}</td>
                            <td style={{padding:'8px 10px',textAlign:'center',fontWeight:700,color:'#E22D2D'}}>{pb.machines_required||'-'}</td>
                            <td style={{padding:'8px 10px',textAlign:'center'}}>{pb.daily_target||'-'}</td>
                            <td style={{padding:'8px 10px',textAlign:'center'}}>{pb.tat_days?pb.tat_days+'d':'-'}</td>
                            <td style={{padding:'8px 10px',fontSize:11,color:'#E22D2D',fontWeight:600}}>{pb.critical_op||'-'}</td>
                            <td style={{padding:'8px 10px'}}>
                              <span style={{padding:'2px 8px',borderRadius:10,fontSize:11,fontWeight:600,
                                background:pb.status==='approved'?'#dcfce7':pb.status==='checked'?'#EEEDF8':'#fef9c3',
                                color:pb.status==='approved'?'#166534':pb.status==='checked'?'#3B3A8F':'#9A6B00'}}>
                                {pb.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* OPERATOR PERFORMANCE */}
            {tab==='operator' && (
              <div>
                <div style={{fontWeight:700,fontSize:14,marginBottom:16,color:'#333'}}>Operator Performance Matrix</div>
                {operatorPerf.length===0 ? (
                  <div style={{padding:40,textAlign:'center',color:'#aaa',border:'1px dashed #eee',borderRadius:8}}>No operator data yet — enter operator names and times in Production Bulletins</div>
                ) : (
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead><tr style={{background:'#F7F6F3'}}>
                      {['Operator','Operation','Machine','Observations','Avg Time','Best Time','Worst Time','Std SAM','Efficiency'].map(h=>(
                        <th key={h} style={{padding:'8px 10px',textAlign:'left',color:'#555',fontWeight:600,borderBottom:'2px solid #eee',fontSize:11}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {operatorPerf.map((op,i)=>(
                        <tr key={i} style={{borderBottom:'1px solid #f5f5f5',background:i%2===0?'white':'#fafafa'}}>
                          <td style={{padding:'8px 10px',fontWeight:700,color:'#3B3A8F'}}>{op.operator_name}</td>
                          <td style={{padding:'8px 10px'}}>{op.process_name}</td>
                          <td style={{padding:'8px 10px'}}>{op.machine_code}</td>
                          <td style={{padding:'8px 10px',textAlign:'center'}}>{op.observations}</td>
                          <td style={{padding:'8px 10px',fontWeight:600}}>{Number(op.avg_time||0).toFixed(2)}m</td>
                          <td style={{padding:'8px 10px',color:'#22c55e',fontWeight:600}}>{Number(op.best_time||0).toFixed(2)}m</td>
                          <td style={{padding:'8px 10px',color:'#ef4444'}}>{Number(op.worst_time||0).toFixed(2)}m</td>
                          <td style={{padding:'8px 10px',color:'#888'}}>{op.standard_sam?op.standard_sam+'m':'-'}</td>
                          <td style={{padding:'8px 10px'}}>
                            {op.efficiency_pct ? (
                              <span style={{padding:'3px 10px',borderRadius:10,fontSize:11,fontWeight:700,background:effBg(op.efficiency_pct),color:effColor(op.efficiency_pct)}}>{op.efficiency_pct}%</span>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}


            {tab==='machines' && (
              <div>
                {/* Summary by category */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:12,marginBottom:20}}>
                  {machinesSummary.map((cat,i)=>(
                    <div key={i} style={{background:'white',border:'1px solid #eee',borderRadius:8,padding:'12px 14px'}}>
                      <div style={{fontSize:11,color:'#888',fontWeight:700,textTransform:'uppercase'}}>{cat.category}</div>
                      <div style={{fontSize:24,fontWeight:900,color:'#3B3A8F',marginTop:4}}>{cat.total}</div>
                      <div style={{fontSize:11,color:'#22c55e'}}>Available: {cat.available}</div>
                      {cat.maintenance>0 && <div style={{fontSize:11,color:'#ef4444'}}>Maintenance: {cat.maintenance}</div>}
                    </div>
                  ))}
                </div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead><tr style={{background:'#F7F6F3'}}>
                    {['Sr','Machine','Code','Category','Total Qty','Available','Maintenance','Action'].map(h=>(
                      <th key={h} style={{padding:'8px 10px',textAlign:'left',color:'#555',fontWeight:600,borderBottom:'2px solid #eee',fontSize:11}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {machinesMaster.map((m,i)=>(
                      <tr key={i} style={{borderBottom:'1px solid #f5f5f5'}}>
                        <td style={{padding:'8px 10px',color:'#888'}}>{m.sr_no}</td>
                        <td style={{padding:'8px 10px',fontWeight:600}}>{m.machine_name}</td>
                        <td style={{padding:'8px 10px',fontFamily:'monospace',fontSize:11,color:'#3B3A8F'}}>{m.machine_code}</td>
                        <td style={{padding:'8px 10px'}}><span style={{padding:'2px 8px',borderRadius:10,fontSize:11,background:'#EEEDF8',color:'#3B3A8F'}}>{m.category}</span></td>
                        <td style={{padding:'8px 10px',textAlign:'center'}}>
                          {editingMachine===m.id ? (
                            <input type="number" min="0" value={machineForm.total_qty} onChange={e=>setMachineForm(p=>({...p,total_qty:e.target.value}))}
                              style={{width:60,padding:'3px 6px',border:'1px solid #3B3A8F',borderRadius:4,fontSize:12}}/>
                          ) : <b>{m.total_qty}</b>}
                        </td>
                        <td style={{padding:'8px 10px',textAlign:'center',color:'#22c55e',fontWeight:600}}>
                          {editingMachine===m.id ? (
                            <input type="number" min="0" value={machineForm.available_qty} onChange={e=>setMachineForm(p=>({...p,available_qty:e.target.value}))}
                              style={{width:60,padding:'3px 6px',border:'1px solid #3B3A8F',borderRadius:4,fontSize:12}}/>
                          ) : m.available_qty}
                        </td>
                        <td style={{padding:'8px 10px',textAlign:'center',color:m.under_maintenance>0?'#ef4444':'#888'}}>
                          {editingMachine===m.id ? (
                            <input type="number" min="0" value={machineForm.under_maintenance} onChange={e=>setMachineForm(p=>({...p,under_maintenance:e.target.value}))}
                              style={{width:60,padding:'3px 6px',border:'1px solid #3B3A8F',borderRadius:4,fontSize:12}}/>
                          ) : (m.under_maintenance||0)}
                        </td>
                        <td style={{padding:'8px 10px'}}>
                          {editingMachine===m.id ? (
                            <div style={{display:'flex',gap:4}}>
                              <button onClick={()=>saveMachine(m.id)} disabled={saving} style={{padding:'3px 10px',background:'#3B3A8F',color:'white',border:'none',borderRadius:4,fontSize:11,cursor:'pointer'}}>✓ Save</button>
                              <button onClick={()=>setEditingMachine(null)} style={{padding:'3px 8px',background:'#f0f2f8',border:'1px solid #ddd',borderRadius:4,fontSize:11,cursor:'pointer'}}>✕</button>
                            </div>
                          ) : (
                            <button onClick={()=>{setEditingMachine(m.id);setMachineForm({total_qty:m.total_qty,available_qty:m.available_qty,under_maintenance:m.under_maintenance||0});}}
                              style={{padding:'3px 10px',background:'#EEEDF8',border:'none',borderRadius:4,color:'#3B3A8F',cursor:'pointer',fontSize:11}}>Edit</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{background:'#f0f2f8',borderTop:'2px solid #3B3A8F',fontWeight:700}}>
                      <td colSpan={4} style={{padding:'8px 10px'}}>TOTAL</td>
                      <td style={{padding:'8px 10px',textAlign:'center'}}>{machinesMaster.reduce((s,m)=>s+(m.total_qty||0),0)}</td>
                      <td style={{padding:'8px 10px',textAlign:'center',color:'#22c55e'}}>{machinesMaster.reduce((s,m)=>s+(m.available_qty||0),0)}</td>
                      <td style={{padding:'8px 10px',textAlign:'center',color:'#ef4444'}}>{machinesMaster.reduce((s,m)=>s+(m.under_maintenance||0),0)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
                <ListFooter shown={machinesMaster.length} total={machinesMaster.length} noun="machines" />
              </div>
            )}
            {/* LINE BALANCING */}
            {tab==='balancing' && (
              <div>
                <div style={{display:'flex',gap:12,alignItems:'center',marginBottom:20}}>
                  <div style={{fontWeight:700,fontSize:14,color:'#333'}}>Line Balancing Simulator</div>
                  <select onChange={e=>e.target.value && loadLineBalance(e.target.value)}
                    style={{padding:'7px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:13}}>
                    <option value="">Select Production Bulletin...</option>
                    {bulletins.map(pb=><option key={pb.id} value={pb.id}>{pb.pb_no} — {pb.gpo_no} — {pb.style}</option>)}
                  </select>
                </div>

                {!lineBalance ? (
                  <div style={{padding:40,textAlign:'center',color:'#aaa',border:'1px dashed #eee',borderRadius:8}}>Select a bulletin to simulate line balancing</div>
                ) : (
                  <div>
                    {/* Summary */}
                    <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:20}}>
                      {[
                        {label:'Total SAM',value:Number(lineBalance.total_sam||0).toFixed(2)+'m',color:'#3B3A8F'},
                        {label:'Output/Machine/Day',value:(lineBalance.output_per_machine||0)+' pcs',color:'#2A2970'},
                        {label:'Daily Target',value:(lineBalance.daily_target||0)+' pcs',color:'#555'},
                        {label:'Machines Needed',value:lineBalance.machines_needed||'-',color:'#E22D2D',big:true},
                        {label:'OT Needed',value:lineBalance.ot_needed>0?lineBalance.ot_needed+' mins':'None',color:lineBalance.ot_needed>0?'#ef4444':'#22c55e'},
                      ].map(card=>(
                        <div key={card.label} style={{background:'white',border:'1px solid #eee',borderRadius:8,padding:'12px 14px',borderTop:card.big?'3px solid #E22D2D':'3px solid #3B3A8F'}}>
                          <div style={{fontSize:10,color:'#888',fontWeight:700,textTransform:'uppercase'}}>{card.label}</div>
                          <div style={{fontSize:card.big?24:18,fontWeight:900,color:card.color,marginTop:4}}>{card.value}</div>
                        </div>
                      ))}
                    </div>

                    {/* Bottleneck */}
                    {lineBalance.bottleneck && (
                      <div style={{background:'#fff5f5',border:'1px solid #fca5a5',borderRadius:8,padding:'12px 16px',marginBottom:20,display:'flex',gap:12,alignItems:'center'}}>
                        <span style={{fontSize:20}}>⚠️</span>
                        <div>
                          <div style={{fontWeight:700,fontSize:13,color:'#E22D2D'}}>BOTTLENECK OPERATION</div>
                          <div style={{fontSize:13}}><b>{lineBalance.bottleneck.process_name}</b> · {lineBalance.bottleneck.effective_sam}m · Machine: {lineBalance.bottleneck.machine_name||lineBalance.bottleneck.machine_code}</div>
                          <div style={{fontSize:11,color:'#888',marginTop:2}}>This operation limits the line speed — consider doubling up this machine</div>
                        </div>
                      </div>
                    )}

                    {/* Machine Groups */}
                    <div style={{fontWeight:700,fontSize:13,marginBottom:12,color:'#333'}}>Suggested Machine Grouping ({lineBalance.machine_groups?.length} machines)</div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:12}}>
                      {(lineBalance.machine_groups||[]).map((mg,i)=>(
                        <div key={i} style={{background:'white',border:'1px solid #eee',borderRadius:8,padding:14}}>
                          <div style={{display:'flex',justifyContent:'space-between',marginBottom:10}}>
                            <div style={{fontWeight:700,color:'#3B3A8F',fontSize:13}}>Machine {mg.machine_no}</div>
                            <div style={{fontSize:12,fontWeight:700,color:mg.total_sam>parseFloat(lineBalance.target_sam_per_machine)*1.1?'#ef4444':'#22c55e'}}>{Number(mg.total_sam||0).toFixed(2)}m</div>
                          </div>
                          {mg.operations.map((op,j)=>(
                            <div key={j} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:'1px solid #f5f5f5',fontSize:11}}>
                              <span style={{color:'#333'}}>{op.process_name}</span>
                              <span style={{fontWeight:600,color:'#3B3A8F'}}>{op.effective_sam}m</span>
                            </div>
                          ))}
                          <div style={{marginTop:8}}>
                            <div style={{height:6,background:'#e5e7eb',borderRadius:3}}>
                              <div style={{height:'100%',width:Math.min(100,(mg.total_sam/parseFloat(lineBalance.target_sam_per_machine||1)*100))+'%',background:mg.total_sam>parseFloat(lineBalance.target_sam_per_machine)*1.1?'#ef4444':'#3B3A8F',borderRadius:3}}/>
                            </div>
                            <div style={{fontSize:10,color:'#888',marginTop:3}}>Target: {lineBalance.target_sam_per_machine}m per machine</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
}

export default function IE() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  return (
    <div style={{minHeight:'100vh',background:'transparent',fontFamily:'inherit'}}>
      
      <IEContent />
    </div>
  );
}
